import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { pageService, pageSectionService, siteConfigService, contactService } from '../services/api';
import AdSlot from './AdSlot';
import { updateMetaTags } from '../utils/seo';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1').replace('/api/v1', '');

function PublicPage({ slugOverride, fallbackSlug = 'home' }) {
  const { slug: routeSlug } = useParams();
  const effectiveSlug = slugOverride || routeSlug || fallbackSlug;
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadPage = async () => {
      setLoading(true);
      try {
        const [pageRes, sectionRes, settingsData] = await Promise.all([
          pageService.show(effectiveSlug).catch(() => ({ data: null })),
          pageSectionService.publicSections(effectiveSlug).catch(() => ({ data: [] })),
          siteConfigService.settings().catch(() => null),
        ]);
        if (!isMounted) return;
        const pageData = pageRes?.data || pageRes || null;
        setPage(pageData);
        setSections(Array.isArray(sectionRes) ? sectionRes : sectionRes?.data || []);
        setSettings(settingsData?.data || settingsData || {});

        if (pageData) {
          updateMetaTags({
            title: pageData.meta_title || pageData.title,
            description: pageData.meta_description,
            keywords: pageData.meta_keywords,
            image: pageData.featured_image,
            favicon: settingsData?.site_favicon,
            siteName: settingsData?.site_name || 'Ortus'
          });
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error loading page:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      isMounted = false;
    };
  }, [effectiveSlug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Loading...</div>;
  if (!page) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Page not found</div>;

  const hasSections = sections.length > 0;

  const imgUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const bgClass = (val) => {
    const map = {
      'white': 'bg-white',
      'slate-50': 'bg-slate-50',
      'red-50': 'bg-red-50',
      'slate-900': 'bg-slate-900 text-white',
    };
    return map[val] || map['white'];
  };

  return (
    <div className="bg-white">
      {/* Hero header */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 text-white py-24 md:py-32 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500 opacity-10 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 opacity-10 blur-3xl rounded-full"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <div className="h-1 w-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mb-6"></div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tight">{page.title}</h1>
            {page.meta_description && (
              <p className="text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed">{page.meta_description}</p>
            )}
          </div>
        </div>
      </section>

      {hasSections ? (
        // Render modern section builder output
        <div className="space-y-0">
          {sections.map((s) => (
            <section key={s.id} className={`${bgClass(s.bg_color)} py-16 md:py-20`}>
              <div className="max-w-7xl mx-auto px-4">
                {s.section_type === 'hero' && (
                  <div className="text-center max-w-4xl mx-auto">
                    {s.subtitle && <p className="text-red-500 font-bold uppercase tracking-[0.2em] text-xs mb-4">{s.subtitle}</p>}
                    {s.image && <img src={imgUrl(s.image)} alt="" className="w-full h-56 md:h-80 object-cover rounded-3xl mb-8 shadow-2xl" />}
                    <h2 className="text-3xl md:text-5xl font-black mb-6 leading-tight">{s.title}</h2>
                    {s.content && <div className="text-lg opacity-80 max-w-3xl mx-auto leading-relaxed" dangerouslySetInnerHTML={{ __html: s.content }} />}
                    {s.button_text && (
                      <a href={s.button_link || '#'} className="inline-flex items-center gap-2 mt-8 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-bold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105">
                        {s.button_text}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                      </a>
                    )}
                  </div>
                )}

                {s.section_type === 'content' && (() => {
                  const meta = s.meta || {};
                  const align = meta.contentAlign || 'left';
                  const width = meta.contentWidth || 'medium';
                  const imgPos = meta.imagePosition || 'none';
                  const imgSize = meta.imageSize || 'large';
                  const imgRatio = meta.imageRatio || 'auto';
                  const imgRadius = meta.imageRadius || 'xl';
                  const padding = meta.padding || 'normal';

                  const widthMap = { narrow: 'max-w-2xl', medium: 'max-w-4xl', wide: 'max-w-5xl', full: 'max-w-7xl' };
                  const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right', justify: 'text-justify' };
                  const paddingMap = { none: 'py-0', sm: 'py-4', normal: 'py-8', lg: 'py-12', xl: 'py-16' };
                  const sizeMap = { small: 'max-w-xs', medium: 'max-w-sm', large: 'max-w-md', full: 'w-full' };
                  const ratioMap = { auto: 'h-auto', square: 'aspect-square', wide: 'aspect-video', tall: 'aspect-[4/5]', landscape: 'aspect-[3/2]', portrait: 'aspect-[2/3]' };
                  const radiusMap = { none: 'rounded-none', sm: 'rounded-sm', md: 'rounded-md', lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl', '3xl': 'rounded-3xl' };

                  const imgEl = s.image && imgPos !== 'none' && (
                    <img src={imgUrl(s.image)} alt="" className={`object-cover ${ratioMap[imgRatio]} ${radiusMap[imgRadius]} ${imgPos === 'full-width' ? 'w-full shadow-xl' : imgPos === 'background' ? 'w-full' : sizeMap[imgSize] + ' mx-auto shadow-lg'} ${imgPos === 'full-width' ? 'h-64 md:h-96' : ''}`} />
                  );

                  const contentEl = (
                    <div>
                      {s.title && (
                        <div className="mb-8">
                          <div className="h-1 w-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mb-5"></div>
                          <h2 className="text-2xl md:text-4xl font-black text-slate-900 leading-tight">{s.title}</h2>
                        </div>
                      )}
                      {s.content && <div className={`prose prose-lg max-w-none text-slate-600 leading-relaxed text-lg ${alignMap[align]} [&_p]:mb-6 [&_p]:leading-relaxed [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_blockquote]:border-l-4 [&_blockquote]:border-red-400 [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-slate-700 [&_.bg-gradient-to-br]:rounded-r-2xl [&_.bg-gradient-to-br]:my-8 [&_img]:rounded-xl [&_img]:shadow-lg [&_a]:text-red-600 [&_a]:font-semibold [&_ul]:list-disc [&_ol]:list-decimal [&_li]:mb-2`} dangerouslySetInnerHTML={{ __html: s.content }} />}
                      {s.button_text && (
                        <a href={s.button_link || '#'} className="inline-flex items-center gap-2 mt-8 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-bold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105">
                          {s.button_text}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                        </a>
                      )}
                    </div>
                  );

                  if (imgPos === 'background' && s.image) {
                    return (
                      <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} relative rounded-3xl overflow-hidden`}>
                        <img src={imgUrl(s.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        {meta.overlay && <div className="absolute inset-0 bg-black/50" style={{ opacity: (parseInt(meta.overlayOpacity || '50') / 100) }} />}
                        <div className={`relative z-10 ${meta.overlay ? 'text-white' : 'text-slate-900'} py-16 px-8 md:px-16 ${alignMap[align]}`}>{contentEl}</div>
                      </div>
                    );
                  }

                  if (imgPos === 'left' || imgPos === 'right') {
                    return (
                      <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>
                        <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center`}>
                          {imgPos === 'left' ? <>{imgEl}{contentEl}</> : <>{contentEl}{imgEl}</>}
                        </div>
                      </div>
                    );
                  }

                  if (imgPos === 'top' && s.image) {
                    return (
                      <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>
                        {imgEl}
                        <div className="mt-8">{contentEl}</div>
                      </div>
                    );
                  }

                  if (imgPos === 'bottom' && s.image) {
                    return (
                      <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>
                        {contentEl}
                        <div className="mt-8">{imgEl}</div>
                      </div>
                    );
                  }

                  if (imgPos === 'full-width' && s.image) {
                    return (
                      <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>
                        {imgEl}
                        <div className="mt-8">{contentEl}</div>
                      </div>
                    );
                  }

                  // Default: no image or 'none'
                  return (
                    <div className={`${widthMap[width]} mx-auto ${paddingMap[padding]} ${alignMap[align]}`}>{contentEl}</div>
                  );
                })()}

                {s.section_type === 'feature_grid' && (
                  <div>
                    {s.title && (
                      <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-4xl font-black mb-3">{s.title}</h2>
                        {s.content && <p className="text-slate-500 max-w-2xl mx-auto">{s.content}</p>}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {(s.meta?.items || []).map((item, idx) => {
                        const cardColors = [
                          { gradient: 'from-orange-500 to-red-500', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
                          { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
                          { gradient: 'from-teal-500 to-cyan-500', bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
                          { gradient: 'from-purple-500 to-pink-500', bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
                          { gradient: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
                          { gradient: 'from-green-500 to-emerald-500', bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
                        ];
                        const c = cardColors[idx % cardColors.length];
                        return (
                          <div
                            key={idx}
                            className={`group relative bg-white rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border ${c.border} overflow-hidden`}
                          >
                            {/* Gradient top bar */}
                            <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${c.gradient}`}></div>

                            {/* Icon */}
                            <div className={`w-16 h-16 ${c.bg} ${c.text} rounded-2xl flex items-center justify-center mx-auto mb-5 text-3xl shadow-sm group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                              {item.icon || '✨'}
                            </div>

                            {/* Title */}
                            <h3 className="font-bold text-lg mb-3 text-slate-900">{item.title || `Feature ${idx + 1}`}</h3>

                            {/* Description */}
                            <p className="text-slate-500 text-sm leading-relaxed">{item.desc || ''}</p>

                            {/* Bottom gradient accent on hover */}
                            <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${c.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {s.section_type === 'image_text' && (() => {
                  const meta = s.meta || {};
                  const reverse = !!meta.reverse;
                  const split = meta.split || '50/50';
                  const align = meta.contentAlign || 'left';
                  const imgSize = meta.imageSize || 'large';
                  const imgRatio = meta.imageRatio || 'auto';
                  const imgRadius = meta.imageRadius || '2xl';
                  const vAlign = meta.vAlign || 'center';
                  const imgShadow = meta.imageShadow || 'lg';

                  const splitMap = { '50/50': 'md:grid-cols-2', '60/40': 'md:grid-cols-[3fr_2fr]', '40/60': 'md:grid-cols-[2fr_3fr]', '70/30': 'md:grid-cols-[7fr_3fr]', '30/70': 'md:grid-cols-[3fr_7fr]' };
                  const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right', justify: 'text-justify' };
                  const ratioMap = { auto: 'h-auto', square: 'aspect-square', wide: 'aspect-video', tall: 'aspect-[4/5]', landscape: 'aspect-[3/2]', portrait: 'aspect-[2/3]' };
                  const radiusMap = { none: 'rounded-none', sm: 'rounded-sm', md: 'rounded-md', lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl', '3xl': 'rounded-3xl' };
                  const shadowMap = { none: 'shadow-none', sm: 'shadow-sm', md: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-2xl' };
                  const vAlignMap = { top: 'items-start', center: 'items-center', bottom: 'items-end' };

                  const imgEl = s.image ? (
                    <img src={imgUrl(s.image)} alt="" className={`w-full object-cover ${ratioMap[imgRatio]} ${radiusMap[imgRadius]} ${shadowMap[imgShadow]} ${imgRatio === 'auto' ? 'h-64 md:h-96' : ''}`} />
                  ) : (
                    <div className={`w-full h-64 md:h-96 bg-slate-200 ${radiusMap[imgRadius]} flex items-center justify-center text-slate-400`}>Add an image in the admin</div>
                  );

                  const textEl = (
                    <div className={alignMap[align]}>
                      {s.title && <h2 className="text-2xl md:text-3xl font-bold mb-4">{s.title}</h2>}
                      {s.content && <div className="text-slate-600 leading-relaxed text-lg prose prose-lg max-w-none [&_p]:mb-4 [&_p]:leading-relaxed [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-6 [&_h2]:mb-3 [&_blockquote]:border-l-4 [&_blockquote]:border-red-400 [&_blockquote]:pl-6 [&_blockquote]:italic [&_a]:text-red-600 [&_a]:font-semibold [&_ul]:list-disc [&_ol]:list-decimal [&_li]:mb-2" dangerouslySetInnerHTML={{ __html: s.content }} />}
                      {s.button_text && (
                        <a href={s.button_link || '#'} className="inline-block mt-6 text-red-600 hover:text-red-700 font-semibold text-sm">
                          {s.button_text} →
                        </a>
                      )}
                    </div>
                  );

                  return (
                    <div className={`grid grid-cols-1 ${splitMap[split]} gap-10 ${vAlignMap[vAlign]}`}>
                      {reverse ? <>{textEl}{imgEl}</> : <>{imgEl}{textEl}</>}
                    </div>
                  );
                })()}

                {s.section_type === 'stats' && (
                  <div>
                    {s.title && <h2 className="text-2xl md:text-4xl font-black text-center mb-12">{s.title}</h2>}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      {(() => {
                        let stats = s.meta?.stats || [];
                        // Only use global settings if no stats are defined in the CMS for this section
                        if (effectiveSlug === 'contact' && settings && (!stats || stats.length === 0)) {
                          const contactAddr = settings.contact_address || settings.data?.contact_address || '';
                          const contactPhone = settings.contact_phone || settings.data?.contact_phone || '';
                          const contactEmail = settings.contact_email || settings.data?.contact_email || '';
                          
                          stats = [
                            { value: contactPhone || 'N/A', label: 'Phone' },
                            { value: contactEmail || 'N/A', label: 'Email' },
                            { value: contactAddr || 'N/A', label: 'Office Address' },
                            { value: 'Mon-Sat', label: '9 AM - 6 PM' },
                          ];
                        }
                        return stats.map((stat, i) => (
                          <div key={i} className="text-center group">
                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-red-500/30 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col justify-center">
                              <div className={`font-black bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent mb-2 group-hover:scale-105 transition-transform duration-300 inline-block break-words whitespace-pre-wrap ${stat.value?.length > 20 ? 'text-lg md:text-xl' : 'text-xl md:text-3xl'}`}>
                                {stat.value}
                              </div>
                              <div className="text-xs md:text-sm text-slate-400 font-semibold uppercase tracking-wider">{stat.label}</div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {s.section_type === 'timeline' && (
                  <div>
                    {s.title && <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{s.title}</h2>}
                    <div className="relative border-l-2 border-red-100 ml-2">
                      {(s.meta?.items || []).map((item, i) => (
                        <div key={i} className="pl-6 pb-10">
                          <div className="absolute -left-1.5 w-3 h-3 bg-red-500 rounded-full mt-2"></div>
                          <p className="text-xs uppercase tracking-wide text-red-600 font-semibold">{item.year}</p>
                          <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                          <p className="text-slate-600 text-sm">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {s.section_type === 'team' && (
                  <div>
                    {s.title && (
                      <div className="text-center mb-12">
                        <div className="h-1 w-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto mb-5"></div>
                        <h2 className="text-2xl md:text-4xl font-black text-slate-900">{s.title}</h2>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                      {(s.meta?.people || []).map((person, idx) => {
                        const avatarColors = [
                          'from-orange-500 to-red-500',
                          'from-blue-500 to-indigo-600',
                          'from-teal-500 to-cyan-500',
                          'from-purple-500 to-pink-500',
                          'from-amber-500 to-orange-500',
                          'from-green-500 to-emerald-500',
                          'from-rose-500 to-red-500',
                          'from-violet-500 to-purple-500',
                        ];
                        const c = avatarColors[idx % avatarColors.length];
                        return (
                          <div
                            key={idx}
                            className="group bg-white rounded-3xl p-6 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-slate-100 text-center relative overflow-hidden"
                          >
                            {/* Gradient top bar */}
                            <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${c}`}></div>

                            {/* Avatar */}
                            <div className="relative mb-5">
                              {person.photo ? (
                                <img src={imgUrl(person.photo)} alt={person.name} className="h-28 w-28 rounded-2xl mx-auto object-cover shadow-lg ring-4 ring-slate-50 group-hover:ring-red-50 transition-all" />
                              ) : (
                                <div className={`h-28 w-28 rounded-2xl mx-auto flex items-center justify-center text-white font-black text-3xl shadow-lg bg-gradient-to-br ${c} group-hover:scale-110 transition-transform duration-300`}>
                                  {(person.name || '').split(' ').map((n) => n[0]).join('')}
                                </div>
                              )}
                            </div>

                            {/* Name */}
                            <h3 className="font-bold text-lg text-slate-900 mb-1">{person.name}</h3>

                            {/* Title */}
                            <p className={`text-sm font-semibold bg-gradient-to-r ${c} bg-clip-text text-transparent mb-3`}>{person.title}</p>

                            {/* Divider */}
                            <div className="w-10 h-0.5 bg-slate-200 mx-auto mb-3 group-hover:bg-red-300 transition-colors"></div>

                            {/* Bio */}
                            <p className="text-slate-500 text-sm leading-relaxed">{person.bio}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {s.section_type === 'faq' && (
                  <div>
                    {s.title && <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{s.title}</h2>}
                    <div className="space-y-4">
                      {(s.meta?.faqs || []).map((faq, idx) => (
                        <details key={idx} className="bg-white border border-slate-200 rounded-xl p-4">
                          <summary className="font-semibold text-slate-900 cursor-pointer">{faq.q}</summary>
                          <p className="mt-2 text-slate-600 text-sm">{faq.a}</p>
                        </details>
                      ))}
                    </div>
                  </div>
                )}

                {s.section_type === 'cta' && (
                  <div className="text-center max-w-3xl mx-auto">
                    <div className="h-1 w-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto mb-6"></div>
                    {s.title && <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight">{s.title}</h2>}
                    {s.content && <div className="text-lg opacity-80 leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: s.content }} />}
                    {s.button_text && (
                      <a href={s.button_link || '#'} className="inline-flex items-center gap-2 border-2 border-red-400 text-red-400 hover:bg-red-400 hover:text-slate-900 font-bold px-8 py-3.5 rounded-xl transition-all hover:scale-105 hover:shadow-lg hover:shadow-red-500/20">
                        {s.button_text}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      ) : (
        // Fallback to HTML content
        <main className="max-w-4xl mx-auto px-4 py-16">
          <div className="prose prose-lg max-w-none text-slate-700">
            {page.content ? (
              <div dangerouslySetInnerHTML={{ __html: page.content }} />
            ) : (
              <p className="text-slate-500">No content available.</p>
            )}
          </div>
        </main>
      )}

      {/* Contact Form */}
      {effectiveSlug === 'contact' && <ContactForm settings={settings} />}

      {/* Public Page Ad Slot */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <AdSlot position="public_bottom" className="h-[200px]" />
      </div>
    </div>
  );
}

function ContactForm({ settings }) {
  const settingsObj = settings?.data || settings || {};
  const contactEmail = settingsObj?.contact_email || 'connect@ortusfnb.com';
  const contactPhone = settingsObj?.contact_phone || '';
  const contactAddress = settingsObj?.contact_address || '';
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      await contactService.submit(form);
      setStatus({ type: 'success', message: 'Thank you for reaching out! We will get back to you within 24 hours.' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setStatus({ type: 'error', message: err.response?.data?.message || 'Something went wrong. Please try again or email us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="max-w-5xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <div className="text-center lg:text-left mb-6">
              <div className="h-1 w-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto lg:mx-0 mb-5"></div>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-3">Get in Touch</h2>
              <p className="text-slate-500 text-sm">We're here to help. Reach out through any of these channels.</p>
            </div>

            {contactPhone && (
              <a href={`tel:${contactPhone}`} className="flex items-center gap-4 bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-red-200 hover:shadow-md transition group">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 group-hover:scale-110 transition-transform">📞</div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Phone</p>
                  <p className="text-slate-900 font-bold">{contactPhone}</p>
                </div>
              </a>
            )}

            {contactEmail && (
              <a href={`mailto:${contactEmail}`} className="flex items-center gap-4 bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-red-200 hover:shadow-md transition group">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 group-hover:scale-110 transition-transform">✉️</div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Email</p>
                  <p className="text-slate-900 font-bold text-sm break-all">{contactEmail}</p>
                </div>
              </a>
            )}

            {contactAddress && (
              <div className="flex items-center gap-4 bg-slate-50 rounded-2xl p-5 border border-slate-100">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0">📍</div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Office Address</p>
                  <p className="text-slate-900 font-bold text-sm whitespace-pre-wrap">{contactAddress}</p>
                </div>
              </div>
            )}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="text-center mb-6">
              <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-2">Send Us a Message</h3>
              <p className="text-slate-500 text-sm">Fill out the form below and our team will get back to you within 24 hours.</p>
            </div>

        {status && (
          <div className={`mb-6 p-4 rounded-2xl text-sm font-medium ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {status.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-slate-50 rounded-3xl p-8 md:p-10 border border-slate-100 shadow-sm space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Full Name *</label>
              <input
                type="text"
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition outline-none"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition outline-none"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition outline-none"
                placeholder="+91 XXXXX XXXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Subject</label>
              <input
                type="text"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition outline-none"
                placeholder="How can we help you?"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">Message *</label>
            <textarea
              name="message"
              required
              rows="5"
              value={form.message}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition outline-none resize-none"
              placeholder="Tell us more about your inquiry..."
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-bold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  Sending...
                </>
              ) : (
                <>
                  Send Message
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                </>
              )}
            </button>
          </div>
        </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PublicPage;
